import React, { Fragment, useState } from 'react'
import { withRouter, Link } from 'react-router-dom'
import { flowRight as compose } from 'lodash'
import { withApollo } from 'react-apollo'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'

import { Icon, LoadingSpinner, Button } from '@paypalcorp/pp-react'

import { isDevOrStageHostname } from '../../utils/helpers'
import { SearchInput } from '../../components/search-bar'
import NoSearchResults from './NoSearchResults'

const ProductSection = glamorous.div({
  paddingTop: '42px',
  display: 'flex',
  flexDirection: 'column',
})

const ProductSectionSpinner = glamorous.div({
  marginLeft: 'auto',
  marginRight: 'auto',
})

const SectionHeaderContainer = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '24px',
  width: '852px',
  '@media only screen and (max-width: 900px)': {
    margin: 'auto 24px 24px',
    width: 'auto',
    alignItems: 'flex-start',
  },
})

const SectionTitle = glamorous.div({
  color: '#212B36',
  fontFamily: '"PayPalSansBig-Medium",Helvetica Neue,Arial,sans-serif',
  fontSize: '24px',
  fontWeight: 500,
  lineHeight: '30px',
  marginBottom: '10px',
})

const SectionSubTitle = glamorous.div({
  color: '#2C2E2F',
  fontFamily: '"PayPalSansBig-Regular",Helvetica Neue,Arial,sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  display: 'inline',
})

const SectionSubtitleLocation = glamorous.div({
  display: 'inline',
  color: '#0070BA',
})

const DesktopButtonContainer = glamorous.div({
  '@media only screen and (max-width: 900px)': {
    display: 'none !important',
  },
})

const MobileButtonLink = glamorous.a({
  color: '#0070ba !important',
  cursor: 'pointer',
  '@media only screen and (min-width: 901px)': {
    display: 'none !important',
  },
})

const ProductDisplay = glamorous.div({
  display: 'flex',
  flexWrap: 'wrap',
  '@media only screen and (max-width: 900px)': {
    margin: 'auto 24px',
  },
})

const Product = glamorous.div({
  width: '254px',
  marginBottom: '30px',
  marginRight: '30px',
})

const ProductImage = glamorous.div({
  width: '254px',
  height: '160px',
  borderRadius: '6px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 2px 0 rgba(0,0,0,0.1)',
})

const Img = glamorous.img({
  width: '254px',
  height: '160px',
  borderRadius: '6px',
})

const ImgCaption = glamorous.div({
  color: '#2C2E2F',
  fontFamily: '"PayPalSansBig-Regular",Helvetica Neue,Arial,sans-serif',
  fontSize: '14px',
  lineHeight: '24px',
  marginTop: '14px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const ImgCaptionLine2 = glamorous.div({
  color: '#6C7378',
})

const IconImgContainer = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const IconImg = glamorous.img({
  height: '94px',
  width: '94px',
  border: '1px solid #EAECED',
  borderRadius: '50%',
  marginBottom: '16px',
})

const IconImgCaption = glamorous.div({
  color: '#2C2E2F',
  fontFamily: '"PayPalSansBig-Regular",Helvetica Neue,Arial,sans-serif',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textAlign: 'center',
})

const EditLocationLink = glamorous.a({
  cursor: 'pointer',
})

export const LocationBasedProductSection = ({
  basepath,
  history,
  title,
  subtitle,
  buttonContent,
  locationName,
  productList,
  displayNameAndAddressCaption,
  editableLocation,
  onClickButton,
  editableSearchLabel,
  editableSearchButtonLabel,
  noSearchResultsTitle,
  noSearchResultsSubtitle,
  noSearchResultsLabel,
  onSaveEditLocation,
  showLoadingSpinner,
  redirectToSearchRouteOnNull,
  currentLocationLabel,
}) => {
  const [isEditingLocation, setIsEditingLocation] = useState(false)
  const DEFAULT_VOUCHER_IMAGE_URL = `https://www.paypalobjects.com/ppcp/images/covid/donations@x600.png`

  const onClickEditLocation = () => {
    setIsEditingLocation(true)
  }

  const renderEditableSubtitle = () => {
    const Text = () => <Fragment>&nbsp;{locationName.trim()}</Fragment>
    if (!locationName.trim()) return null
    if (!editableLocation) return <Text />
    return (
      <EditLocationLink onClick={onClickEditLocation}>
        <Text />
        <Icon name="edit" size="xs" />
      </EditLocationLink>
    )
  }

  const onLocationUpdate = (value) => {
    if (value) onSaveEditLocation(value)
    setIsEditingLocation(false)
  }

  const getDonationLink = (sku) => {
    let baseUrl = 'https://www.paypal.com/biz/fund?id='
    if (isDevOrStageHostname()) {
      baseUrl =
        'https://www.msmaster.qa.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id='
    }
    return `${baseUrl}${sku}`
  }

  const renderProductLocation = (product) => {
    if (!product.address) return null

    const { city, country, line1, line2, postalCode, state } = product.address

    const padder = (s, pad = ' ') => (s && `${s}${pad}`) || ''
    const address = `${padder(city, ', ')}${padder(state)}${padder(
      country,
    )}${padder(postalCode, '')}`.trim()

    return (
      <Fragment>
        <ImgCaptionLine2>{address}</ImgCaptionLine2>
        {line1 && <ImgCaptionLine2>{line1}</ImgCaptionLine2>}
        {line2 && <ImgCaptionLine2>{line2}</ImgCaptionLine2>}
      </Fragment>
    )
  }

  if (!productList || productList.length === 0) {
    if (redirectToSearchRouteOnNull) {
      history.push(`/business/search/?q=${encodeURIComponent(locationName)}`)
    }
    return (
      <NoSearchResults
        title={noSearchResultsTitle}
        subtitle={noSearchResultsSubtitle}
        searchLabel={noSearchResultsLabel}
        onSubmit={onSaveEditLocation}
        enableSeeYourLocation={true}
        currentLocationLabel={currentLocationLabel}
      />
    )
  }

  return (
    <Fragment>
      <ProductSection>
        <SectionHeaderContainer>
          <div>
            <SectionTitle>{title}</SectionTitle>
            {isEditingLocation ? (
              <SearchInput
                placeholder={editableSearchLabel}
                inNav={false}
                submitLabel={editableSearchButtonLabel}
                onSubmit={onLocationUpdate}
                enableSeeYourLocation={true}
                currentLocationLabel={currentLocationLabel}
              />
            ) : (
              <SectionSubTitle>
                {subtitle.trim()}
                <SectionSubtitleLocation>
                  {renderEditableSubtitle()}
                </SectionSubtitleLocation>
              </SectionSubTitle>
            )}
          </div>
          {!editableLocation && buttonContent && (
            <div>
              <DesktopButtonContainer>
                <Button secondary size="md" onClick={onClickButton}>
                  {buttonContent}
                </Button>
              </DesktopButtonContainer>
              <MobileButtonLink onClick={onClickButton}>
                {buttonContent}
              </MobileButtonLink>
            </div>
          )}
        </SectionHeaderContainer>
        <ProductDisplay>
          {productList.map((product, idx) => (
            <Product key={product.id}>
              <ProductImage>
                {product.sku ? (
                  <a href={getDonationLink(product.sku)} target={`_blank`}>
                    <IconImgContainer>
                      <IconImg
                        src={product.img_url || DEFAULT_VOUCHER_IMAGE_URL}
                        alt={product.name}
                      />
                      <IconImgCaption>{product.name}</IconImgCaption>
                    </IconImgContainer>
                  </a>
                ) : (
                  <Link
                    tabIndex={idx + 1}
                    to={`${basepath || ''}${product.url_key}`}
                  >
                    <Img src={product.img_url} alt={product.name} />
                  </Link>
                )}
              </ProductImage>
              {displayNameAndAddressCaption && (
                <ImgCaption>
                  {product.name}
                  {renderProductLocation(product)}
                </ImgCaption>
              )}
            </Product>
          ))}
        </ProductDisplay>
        {showLoadingSpinner && (
          <ProductSectionSpinner>
            <LoadingSpinner />
          </ProductSectionSpinner>
        )}
      </ProductSection>
    </Fragment>
  )
}

LocationBasedProductSection.propTypes = {
  basepath: PropTypes.string,
  history: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonContent: PropTypes.element,
  locationName: PropTypes.string,
  productList: PropTypes.array,
  displayNameAndAddressCaption: PropTypes.bool,
  editableLocation: PropTypes.bool,
  onClickButton: PropTypes.func,
  editableSearchLabel: PropTypes.string,
  editableSearchButtonLabel: PropTypes.string,
  noSearchResultsTitle: PropTypes.string,
  noSearchResultsSubtitle: PropTypes.string,
  noSearchResultsLabel: PropTypes.string,
  onSaveEditLocation: PropTypes.func,
  showLoadingSpinner: PropTypes.bool,
  redirectToSearchRouteOnNull: PropTypes.bool,
  currentLocationLabel: PropTypes.string,
}

const LocationBasedProductSectionWithGraphql = compose(withApollo)(
  withRouter(LocationBasedProductSection),
)

export default LocationBasedProductSectionWithGraphql
